import { i18n } from "@/i18n";
import { SIDENAV_GROUP_BY } from "@/constants";
import { PHISH_SERVICE, SEG_SERVICE } from "@/utils/services";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

import { useFlag } from "@/composables/useFlags/useFlag";

const { isFeatureAvailable: isQuarantineConfigurationAvailable } = useFlag(
  "QUARANTINE_CONFIGURATION",
);

const { isFeatureAvailable: isGeoblockingConfigPage } = useFlag(
  "GEOBLOCKING_CONFIG_PAGE",
);

export const MSP_NAV_ITEMS = [
  {
    order: 1,
    label: i18n.global.t("email-security-solution"),
    groupBy: SIDENAV_GROUP_BY.BETA,
    icon: "cb-icon-email",
    id: buildOrgUrl("msp", null, "email_security"),
    dataTestId: "msp-phish-titan",
    solutionId: PHISH_SERVICE,
    children: [
      {
        label: i18n.global.t("overview"),
        id: buildOrgUrl("msp", null, "email_security/overview"),
        dataTestId: "msp-email_security-overview",
      },
      {
        label: i18n.global.t("email-history"),
        id: buildOrgUrl("msp", null, "email_security/email-history"),
        dataTestId: "msp-email_security-email_history",
      },
      {
        label: i18n.global.t("quarantine-title"),
        id: buildOrgUrl("msp", null, "email_security/quarantine"),
        dataTestId: "msp-email_security-quarantine",
        solutionId: SEG_SERVICE,
      },

      {
        label: i18n.global.t("incidents-legend"),
        id: buildOrgUrl("msp", null, "email_security/incidents"),
        dataTestId: "msp-email_security-incidents",
        permanentPill: { variant: "infoFull", text: i18n.global.t("AI") },
      },
      {
        label: i18n.global.t("configuration"),
        id: buildOrgUrl("msp", null, "email_security/configuration"),
        dataTestId: "msp-email_security-configuration",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("mail-flow-title"),
            id: buildOrgUrl(
              "msp",
              null,
              "email_security/configuration/mail_flow",
            ),
            dataTestId: "msp-email_security-configuration-mail-flow",
          },
          {
            label: i18n.global.t("exemptions-title"),
            id: buildOrgUrl(
              "msp",
              null,
              "email_security/configuration/exemptions",
            ),
            dataTestId: "msp-mail-flow-tab-exemptions",
          },
          ...(isQuarantineConfigurationAvailable.value
            ? [
                {
                  label: i18n.global.t("quarantine-title"),
                  id: buildOrgUrl(
                    "msp",
                    null,
                    "email_security/configuration/quarantine",
                  ),
                  dataTestId: "msp-mail-flow-tab-quarantine",
                  solutionId: SEG_SERVICE,
                },
              ]
            : []),
          {
            label: i18n.global.t("block-list"),
            id: buildOrgUrl(
              "msp",
              null,
              "email_security/configuration/block_list",
            ),
            dataTestId: "msp-mail-flow-tab-block-list",
            solutionId: SEG_SERVICE,
          },
          ...(isGeoblockingConfigPage.value
            ? [
                {
                  label: i18n.global.t("geoblocking-tab"),
                  id: buildOrgUrl(
                    "msp",
                    null,
                    "email_security/configuration/geoblocking",
                  ),
                  dataTestId: "msp-mail-flow-tab-geoblocking",
                  solutionId: SEG_SERVICE,
                },
              ]
            : []),
          {
            label: i18n.global.t("link-lock-title"),
            id: buildOrgUrl(
              "msp",
              null,
              "email_security/configuration/link_lock",
            ),
            dataTestId: "msp-email_security-configuration-link-lock",
          },
        ],
      },
      {
        label: i18n.global.t("notifications-sidebar"),
        id: buildOrgUrl("msp", null, "email_security/notifications"),
        dataTestId: "msp-email_security-notifications",
      },
      {
        label: i18n.global.t("reports"),
        id: buildOrgUrl("msp", null, "email_security/reports"),
        dataTestId: "msp-email_security-reports",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("summary"),
            id: buildOrgUrl("msp", null, "email_security/reports/summary"),
            dataTestId: "msp-email_security-reports-summary",
          },
          {
            label: i18n.global.t("insights"),
            id: buildOrgUrl("msp", null, "email_security/reports/insights"),
            dataTestId: "msp-email_security-reports-insights",
          },
        ],
      },
    ],
  },
];

export const CUSTOMER_NAV_ITEMS = [
  {
    order: 1,
    label: i18n.global.t("email-security-solution"),
    groupBy: SIDENAV_GROUP_BY.BETA,
    icon: "cb-icon-email",
    id: buildOrgUrl("customer", null, "email_security"),
    dataTestId: "customer-phish-titan",
    solutionId: PHISH_SERVICE,
    children: [
      {
        label: i18n.global.t("overview"),
        id: buildOrgUrl("customer", null, "email_security/overview"),
        dataTestId: "customer-email_security-overview",
      },

      {
        label: i18n.global.t("email-history"),
        id: buildOrgUrl("customer", null, "email_security/email-history"),
        dataTestId: "customer-email_security-email_history",
      },

      {
        label: i18n.global.t("quarantine-title"),
        id: buildOrgUrl("customer", null, "email_security/quarantine"),
        dataTestId: "customer-email_security-quarantine",
        solutionId: SEG_SERVICE,
      },
      {
        label: i18n.global.t("incidents-legend"),
        id: buildOrgUrl("customer", null, "email_security/incidents"),
        dataTestId: "customer-email_security-incidents",
        permanentPill: { variant: "infoFull", text: i18n.global.t("AI") },
      },
      {
        label: i18n.global.t("domains"),
        id: buildOrgUrl("customer", null, "email_security/domains"),
        dataTestId: "customer-email_security-domains",
        solutionId: SEG_SERVICE,
      },
      {
        label: i18n.global.t("configuration"),
        id: buildOrgUrl("customer", null, "email_security/configuration"),
        disabled: false,
        dataTestId: "customer-email_security-configuration",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("mail-flow-title"),
            id: buildOrgUrl(
              "customer",
              null,
              "email_security/configuration/mail_flow",
            ),
            dataTestId: "customer-email_security-configuration-mail-flow",
          },
          {
            label: i18n.global.t("exemptions-title"),
            id: buildOrgUrl(
              "customer",
              null,
              "email_security/configuration/exemptions",
            ),

            dataTestId: "customer-mail-flow-tab-exemptions",
          },
          ...(isQuarantineConfigurationAvailable.value
            ? [
                {
                  label: i18n.global.t("quarantine-title"),
                  id: buildOrgUrl(
                    "customer",
                    null,
                    "email_security/configuration/quarantine",
                  ),
                  dataTestId: "customer-mail-flow-tab-quarantine",
                  solutionId: SEG_SERVICE,
                },
              ]
            : []),
          {
            label: i18n.global.t("block-list"),
            id: buildOrgUrl(
              "customer",
              null,
              "email_security/configuration/block_list",
            ),
            dataTestId: "customer-mail-flow-tab-block-list",
            solutionId: SEG_SERVICE,
          },
          ...(isGeoblockingConfigPage.value
            ? [
                {
                  label: i18n.global.t("geoblocking-tab"),
                  id: buildOrgUrl(
                    "customer",
                    null,
                    "email_security/configuration/geoblocking",
                  ),
                  dataTestId: "customer-mail-flow-tab-geoblocking",
                  solutionId: SEG_SERVICE,
                },
              ]
            : []),
          {
            label: i18n.global.t("link-lock-title"),
            id: buildOrgUrl(
              "customer",
              null,
              "email_security/configuration/link_lock",
            ),
            dataTestId: "customer-email_security-link-lock",
          },
        ],
      },
      {
        label: i18n.global.t("notifications-sidebar"),
        id: buildOrgUrl("customer", null, "email_security/notifications"),
        dataTestId: "customer-email_security-notifications",
      },
      {
        label: i18n.global.t("reports"),
        id: buildOrgUrl("customer", null, "email_security/reports"),
        dataTestId: "customer-email_security-reports",
        hasEvent: true,
        children: [
          {
            label: i18n.global.t("summary"),
            id: buildOrgUrl("customer", null, "email_security/reports/summary"),
            dataTestId: "customer-email_security-reports-summary",
          },
          {
            label: i18n.global.t("insights"),
            id: buildOrgUrl(
              "customer",
              null,
              "email_security/reports/insights",
            ),
            dataTestId: "customer-email_security-reports-insights",
          },
        ],
      },
    ],
  },
];

export default {};
